'use client'

import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import { LayoutGroup, motion } from 'framer-motion'
import React, { forwardRef, useId } from 'react'
import { TouchTarget } from './button'
import { Link } from './link'
import NotificationBell from '@/components/NotificationBell'
import EmailVerificationBanner from '@/components/EmailVerificationBanner'
import { useNotifications } from '@/context/NotificationContext'

// --- Imports for Avatar Logic ---
import { useAuth } from '@/context/AuthContext'
import useEntitlements from '@/hooks/useEntitlements'
import { Avatar } from '@/components/avatar'
import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
  DropdownLabel,
  DropdownDivider
} from '@/components/Dropdown'
import { 
  UserIcon, 
  Cog8ToothIcon, 
  QuestionMarkCircleIcon, 
  ArrowRightStartOnRectangleIcon 
} from '@heroicons/react/24/outline'

export function Navbar({ className, ...props }) {
  useNotifications()

  // --- Hooks ---
  const { userProfile, firebaseUser, logout } = useAuth()
  const { isPro, isFounder } = useEntitlements(firebaseUser?.uid || null)

  const handleLogout = async () => {
    try {
      await logout()
      window.location.href = '/login'
    } catch (err) {
      console.error('Logout failed:', err)
    }
  }

  // Define Colors
  const PALETTE_PRIMARY = '#3483FE'

  return (
    <>
      <EmailVerificationBanner />
      <nav
        {...props}
        className={clsx(
          className,
          'relative flex flex-1 items-center justify-between gap-4 py-2.5 px-4 lg:px-6 border-b border-zinc-950/10 dark:border-white/10'
        )}
      >
        {/* Left side */}
        <div className="flex items-center gap-3">
          {props.children}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-3 md:gap-4">
          <div>
            <NotificationBell className="relative inline-flex p-2 rounded-full text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:text-zinc-200 dark:hover:bg-zinc-800 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#3483FE]" />
          </div>

          {/* --- Mobile Avatar Dropdown (Hidden on Large Screens) --- */}
          <div className="lg:hidden flex items-center">
            {userProfile ? (
               <Dropdown>
                 <DropdownButton as="button" className="relative outline-none">
                   
                    {/* CONTAINER FOR AVATAR + PILL */}
                    <div className="relative">
                      
                      {/* --- PRO PILL --- */}
                      {isPro && (
                          <>
                             <span
                               className={clsx(
                                 "absolute -top-1.5 left-1/2 z-30 -translate-x-1/2 transform rounded-full px-1.5 py-0.5 text-[8px] font-bold shadow-md border border-white whitespace-nowrap",
                                 // Gold Text if Founder, White if just Pro
                                 isFounder ? "text-amber-900" : "text-white"
                               )}
                               style={{ 
                                 background: isFounder 
                                  ? 'linear-gradient(to right, #FFD700, #FDB931)' // Gold Gradient
                                  : PALETTE_PRIMARY // Blue
                               }}
                             >
                               PRO
                             </span>
                          </>
                      )}

                      <Avatar
                        src={userProfile.photoURL || firebaseUser?.photoURL || '/default-avatar.png'}
                        className="size-8 rounded-full relative z-20"
                        style={{
                           // Founder Gold vs Pro Blue Ring
                           boxShadow: isFounder 
                             ? '0 0 0 2px white, 0 0 0 4px #FBBF24' 
                             : isPro ? `0 0 0 2px white, 0 0 0 4px ${PALETTE_PRIMARY}` : 'none',
                        }}
                      />
                    </div>

                 </DropdownButton>

                 {/* FIX APPLIED: 
                    1. Removed 'anchor' prop entirely to disable auto-positioning.
                    2. Added 'absolute', 'top-full', '!right-0' to force right-edge alignment.
                 */}
                 <DropdownMenu 
                    className="absolute top-full !right-0 !left-auto mt-2 z-50 min-w-48 origin-top-right"
                 >
                   {/* Header with Status */}
                   <div className="px-3 py-2 border-b border-zinc-100 dark:border-white/5">
                       <div className="text-sm font-medium text-zinc-900 dark:text-white truncate">
                          {userProfile.displayName}
                       </div>
                       {isFounder ? (
                          <div className="flex items-center gap-1 mt-1">
                             <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider bg-amber-50 px-1.5 py-0.5 rounded border border-amber-100 flex items-center gap-1">
                               Founder
                               <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor">
                                 <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" />
                                 <path d="M5 16h14a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2z" />
                               </svg>
                             </span>
                          </div>
                       ) : isPro ? (
                           <div className="flex items-center gap-1 mt-1">
                              <span className="text-[10px] font-bold text-blue-500 uppercase tracking-wider bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100">
                                 Pro
                              </span>
                           </div>
                       ) : null}
                   </div>

                   <DropdownItem href="/profile">
                     <UserIcon className="size-4" />
                     <DropdownLabel>My Profile</DropdownLabel>
                   </DropdownItem>
                   <DropdownItem href="/settings">
                     <Cog8ToothIcon className="size-4" />
                     <DropdownLabel>Settings</DropdownLabel>
                   </DropdownItem>
                   <DropdownDivider />
                   <DropdownItem href="/support">
                     <QuestionMarkCircleIcon className="size-4" />
                     <DropdownLabel>Help</DropdownLabel>
                   </DropdownItem>
                   <DropdownDivider />
                   <DropdownItem onClick={handleLogout}>
                     <ArrowRightStartOnRectangleIcon className="size-4" />
                     <DropdownLabel>Sign out</DropdownLabel>
                   </DropdownItem>
                 </DropdownMenu>
               </Dropdown>
            ) : null}
          </div>

        </div>
      </nav>
    </>
  )
}

/* -------- helpers (unchanged) -------- */
export function NavbarDivider({ className, ...props }) {
  return <div aria-hidden="true" {...props} className={clsx(className, 'h-6 w-px bg-zinc-950/10 dark:bg-white/10')} />
}

export function NavbarSection({ className, ...props }) {
  const id = useId()
  return (
    <LayoutGroup id={id}>
      <div {...props} className={clsx(className, 'flex items-center gap-3')} />
    </LayoutGroup>
  )
}

export function NavbarSpacer({ className, ...props }) {
  return <div aria-hidden="true" {...props} className={clsx(className, '-ml-4 flex-1')} />
}

export const NavbarItem = forwardRef(function NavbarItem(
  { current, className, children, ...props },
  ref
) {
  const classes = clsx(
    'relative flex min-w-0 items-center gap-3 rounded-lg p-2 text-left text-base/6 font-medium text-zinc-950 sm:text-sm/5',
    '*:data-[slot=icon]:size-6 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:fill-zinc-500 sm:*:data-[slot=icon]:size-5',
    '*:not-nth-2:last:data-[slot=icon]:ml-auto *:not-nth-2:last:data-[slot=icon]:size-5 sm:*:not-nth-2:last:data-[slot=icon]:size-4',
    '*:data-[slot=avatar]:-m-0.5 *:data-[slot=avatar]:size-7 *:data-[slot=avatar]:[--avatar-radius:var(--radius-md)] *:data-[slot=avatar]:[--ring-opacity:10%] sm:*:data-[slot=avatar]:size-6',
    'data-hover:bg-zinc-950/5 data-hover:*:data-[slot=icon]:fill-zinc-950',
    'data-active:bg-zinc-950/5 data-active:*:data-[slot=icon]:fill-zinc-950',
    'dark:text-white dark:*:data-[slot=icon]:fill-zinc-400',
    'dark:data-hover:bg-white/5 dark:data-hover:*:data-[slot=icon]:fill-white',
    'dark:data-active:bg-white/5 dark:data-active:*:data-[slot=icon]:fill-white'
  )

  return (
    <span className={clsx(className, 'relative')}>
      {current && (
        <motion.span
          layoutId="current-indicator"
          className="absolute inset-x-2 -bottom-2.5 h-0.5 rounded-full bg-[#3483FE]"
        />
      )}
      {'href' in props ? (
        <Link {...props} className={classes} data-current={current ? 'true' : undefined} ref={ref}>
          <TouchTarget>{children}</TouchTarget>
        </Link>
      ) : (
        <Headless.Button
          {...props}
          className={clsx('cursor-default', classes)}
          data-current={current ? 'true' : undefined}
          ref={ref}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      )}
    </span>
  )
})

export function NavbarLabel({ className, ...props }) {
  return <span {...props} className={clsx(className, 'truncate')} />
}